import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Rocket, Globe, Loader2, CheckCircle2, XCircle, ExternalLink, PenSquare } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useSite } from "@/context/SiteContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Per-page deploy actions folded into the top bar. Site-aware: every job
 * carries the active workspace's site_id so it deploys with that
 * workspace's own credentials. Refreshes the health scorecard on finish.
 */
export const DeployActions = ({ target, label }) => {
  const { activeSite, fetchHealth } = useSite();
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = (jobId, mode) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/wp/deploy/${jobId}`);
        setJob(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
          if (activeSite) fetchHealth(activeSite.id, true); // refresh scorecard/alerts
          if (data.status === "completed") {
            if (mode === "live") toast.success(`${label} is LIVE \u2014 the public page has been updated.`);
            else toast.success(`${label} deployed as draft \u2014 ready in WordPress.`);
          } else toast.error("Deployment failed \u2014 see the full log on the Deploy Engine page.");
        }
      } catch (e) {
        /* keep polling */
      }
    }, 2000);
  };

  const start = async (mode) => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/wp/deploy`, {
        target,
        mode,
        confirm: mode === "live",
        site_id: activeSite?.id || null,
      });
      setJob({ id: data.job_id, status: "queued", steps: [], mode });
      poll(data.job_id, mode);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start deployment.");
    } finally {
      setStarting(false);
    }
  };

  const running = job && (job.status === "queued" || job.status === "running");
  const isLive = job?.mode === "live" || job?.result?.mode === "live";

  if (running) {
    return (
      <div data-testid="topbar-deploy-progress" className={`flex items-center gap-1.5 ${isLive ? "text-[#0050c0]" : "text-[#b45309]"}`}>
        <Loader2 size={13} className="animate-spin" />
        <span className="font-nunito text-[9px] font-extrabold uppercase tracking-[0.14em] whitespace-nowrap">
          {isLive ? "Overwriting live\u2026" : "Deploying draft\u2026"}
        </span>
      </div>
    );
  }

  if (job?.status === "completed" && job.result) {
    return (
      <div data-testid="topbar-deploy-success" className="flex items-center gap-2">
        <span className="flex items-center gap-1 text-[#0050c0] font-nunito text-[9px] font-extrabold uppercase tracking-[0.14em] whitespace-nowrap">
          <CheckCircle2 size={13} /> {job.result.mode === "live" ? "Live updated" : "Draft ready"}
        </span>
        {job.result.mode === "live" && job.result.live_link && (
          <a href={job.result.live_link} target="_blank" rel="noopener noreferrer" data-testid="topbar-deploy-view-live-link" className="flex items-center gap-1 text-[#6e6e6e] hover:text-[#383838] text-[10px] transition-colors duration-150">
            <ExternalLink size={11} /> View
          </a>
        )}
        <a href={job.result.edit_link} target="_blank" rel="noopener noreferrer" data-testid="topbar-deploy-edit-link" className="flex items-center gap-1 text-[#6e6e6e] hover:text-[#383838] text-[10px] transition-colors duration-150">
          <PenSquare size={11} /> Edit
        </a>
        <button type="button" data-testid="topbar-deploy-reset-button" onClick={() => setJob(null)} className="text-[#9a9a9a] hover:text-[#383838] text-[10px] transition-colors duration-150">
          New
        </button>
      </div>
    );
  }

  if (job?.status === "failed") {
    return (
      <div data-testid="topbar-deploy-failed" className="flex items-center gap-2">
        <span className="flex items-center gap-1 text-[#c25a00] font-nunito text-[9px] font-extrabold uppercase tracking-[0.14em]">
          <XCircle size={13} /> Failed
        </span>
        <button type="button" data-testid="topbar-deploy-retry-button" onClick={() => start(job?.mode || "draft")} className="rounded-[8px] bg-[#f07000] px-2.5 py-1 font-nunito text-[9px] font-extrabold uppercase tracking-[0.12em] text-white hover:bg-[#d96400] transition-colors duration-150">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => start("draft")}
        disabled={starting}
        data-testid="topbar-deploy-draft-button"
        className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#f07000] px-2.5 py-1.5 font-nunito text-[9px] font-extrabold uppercase tracking-[0.12em] text-white hover:bg-[#d96400] active:scale-[0.98] transition-colors duration-150 disabled:opacity-60"
      >
        {starting ? <Loader2 size={11} className="animate-spin" /> : <Rocket size={11} />}
        Draft
      </button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button
            type="button"
            disabled={starting}
            data-testid="topbar-deploy-live-button"
            className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#0050c0] px-2.5 py-1.5 font-nunito text-[9px] font-extrabold uppercase tracking-[0.12em] text-white hover:bg-[#0343a0] active:scale-[0.98] transition-colors duration-150 disabled:opacity-60"
          >
            <Globe size={11} />
            Live
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid="topbar-deploy-live-confirm" className="rounded-[14px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-nunito font-extrabold text-[#383838]">
              Overwrite the live “{label}” page?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6e6e6e]">
              This immediately replaces the content of the <b>live, public</b> page on {activeSite?.production_domain || "the connected site"}.
              WordPress keeps a revision so you can roll back from wp-admin if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="topbar-deploy-live-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="topbar-deploy-live-confirm-yes"
              onClick={() => start("live")}
              className="bg-[#0050c0] hover:bg-[#0343a0] text-white font-nunito font-extrabold uppercase tracking-[0.08em] text-[12px]"
            >
              Yes, Overwrite Live
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
